import type { Timestamp, FieldValue } from 'firebase/firestore';

/**
 * Format a Firestore timestamp for display in notifications
 */
export function formatNotificationDate(
  timestamp: Timestamp | FieldValue | { toDate: () => Date }
): string {
  if (!timestamp || !('toDate' in timestamp)) {
    return 'Just now';
  }
  
  const date = timestamp.toDate();
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Less than 1 minute
  if (diffMins < 1) {
    return 'Just now';
  }
  
  // Less than 1 hour
  if (diffMins < 60) {
    return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  }
  
  // Less than 24 hours
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  }
  
  // Less than 7 days
  if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }
  
  // Show full date
  return date.toLocaleDateString();
}

/**
 * Replace route parameters with actual values
 * Example: replaceRouteParams('/org/:slug', { slug: 'my-org' }) => '/org/my-org'
 */
export function replaceRouteParams(
  route: string,
  params: Record<string, string>
): string {
  let result = route;
  
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`:${key}`, value);
  });
  
  return result;
}
